import React, { useMemo, useRef, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { MagnifyingGlass, ClipboardText, X } from "@phosphor-icons/react";
import { FA_ICONS, FA_CATEGORIES } from "@/lib/faIcons";
import { toast } from "sonner";

/**
 * Font Awesome icon picker — popover with a searchable, category-filterable
 * grid of curated FA Solid + Brands icons (courier-relevant set, ~36 icons).
 *
 * Two operating modes via the `mode` prop:
 *  - "insert" (default): calls `onPick(svgString)` so the caller can drop the
 *    inline SVG into a contentEditable body at the cursor position.
 *  - "copy": shows a copy-to-clipboard button on each icon so users can paste
 *    the SVG into an Unlayer "HTML" block inside the EmailDesigner. Useful
 *    when the editor isn't directly addressable from React.
 *
 * Icons are inserted as inline `<svg>` strings with `currentColor` fills, so
 * the surrounding text colour propagates naturally — email-client safe (no
 * web-font dependency that would get stripped by Gmail/Outlook).
 */
export function FaIconPicker({
  mode = "insert",
  onPick,
  triggerLabel = "Insert icon",
  triggerVariant = "outline",
  testId = "fa-icon-picker",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");
  const inputRef = useRef(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return FA_ICONS.filter((i) => {
      if (category !== "All" && i.category !== category) return false;
      if (!q) return true;
      return (
        i.id.toLowerCase().includes(q) ||
        i.label.toLowerCase().includes(q) ||
        i.category.toLowerCase().includes(q)
      );
    });
  }, [query, category]);

  const handleClick = async (icon) => {
    // Always work with the base64 data-URI <img> payload — it survives both
    // Unlayer's HTML escape AND the rich-text contentEditable insert, and
    // renders in every email client. Raw <svg> markup would be HTML-escaped
    // into literal text on insert.
    const payload = icon.img;
    if (mode === "copy") {
      try {
        await navigator.clipboard.writeText(payload);
        toast.success(`Copied "${icon.label}" — paste into the designer`);
      } catch {
        toast.error("Clipboard blocked — try the insert mode instead");
      }
      return;
    }
    onPick?.(payload);
    setOpen(false);
    setQuery("");
  };

  return (
    <Popover
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (v) setTimeout(() => inputRef.current?.focus(), 50);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant={triggerVariant}
          size="sm"
          type="button"
          data-testid={`${testId}-trigger`}
          className="gap-1.5"
          title="Insert a Font Awesome icon"
        >
          {/* Tiny FA logo glyph */}
          <span
            className="inline-block"
            dangerouslySetInnerHTML={{
              __html: FA_ICONS.find((i) => i.id === "envelope")?.svg.replace("16", "13").replace("16", "13") || "",
            }}
          />
          {triggerLabel}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="p-0 w-[min(540px,calc(100vw-2rem))] max-h-[70vh] overflow-hidden flex flex-col"
        data-testid={`${testId}-popover`}
      >
        <div className="flex items-center gap-2 px-3 py-2 border-b border-zinc-100">
          <MagnifyingGlass size={14} className="text-zinc-400" />
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search icons (envelope, truck, instagram, etc.)"
            className="border-0 shadow-none focus-visible:ring-0 px-0 h-7"
            data-testid={`${testId}-input`}
          />
          {query && (
            <button onClick={() => setQuery("")} className="text-zinc-400 hover:text-zinc-700">
              <X size={14} />
            </button>
          )}
        </div>
        <div className="flex gap-1 px-3 py-2 border-b border-zinc-100 overflow-x-auto">
          {["All", ...FA_CATEGORIES].map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCategory(c)}
              className={`shrink-0 px-2 py-1 rounded text-[10px] font-semibold uppercase tracking-wider transition-colors ${
                category === c
                  ? "bg-[#002BF6] text-white"
                  : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"
              }`}
              data-testid={`${testId}-cat-${c}`}
            >
              {c}
            </button>
          ))}
        </div>
        <div className="overflow-y-auto p-2 grid grid-cols-6 gap-1.5">
          {filtered.length === 0 ? (
            <div className="col-span-6 px-3 py-8 text-center text-xs text-zinc-500">
              No icons match.
            </div>
          ) : (
            filtered.map((icon) => (
              <button
                key={icon.id}
                type="button"
                onClick={() => handleClick(icon)}
                title={`${icon.label}${mode === "copy" ? " — click to copy SVG" : ""}`}
                data-testid={`${testId}-icon-${icon.id}`}
                className="aspect-square flex flex-col items-center justify-center gap-1 rounded border border-transparent hover:border-[#002BF6] hover:bg-blue-50 transition-colors p-1 group"
              >
                <span
                  className="text-zinc-700 group-hover:text-[#002BF6]"
                  dangerouslySetInnerHTML={{
                    __html: icon.svg.replace('width="16"', 'width="20"').replace('height="16"', 'height="20"'),
                  }}
                />
                <span className="text-[9px] text-zinc-500 truncate w-full text-center leading-none">
                  {icon.label.split(" ")[0]}
                </span>
              </button>
            ))
          )}
        </div>
        <div className="px-3 py-2 border-t border-zinc-100 text-[10px] text-zinc-500 flex items-center gap-1.5">
          {mode === "copy" ? (
            <>
              <ClipboardText size={11} weight="bold" /> Click any icon to copy its SVG, then paste into an HTML block.
            </>
          ) : (
            <>{filtered.length} of {FA_ICONS.length} icons · click to insert into your body</>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
